import { CommonModule } from "@angular/common";
import { Component, OnInit, computed, inject, input } from "@angular/core";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { TruncateTextComponent } from "@tylertech/common-components-module";
import { IMenuOption, IMenuSelectEventData } from "@tylertech/forge";
import { ForgeIconButtonModule, ForgeIconModule, ForgeMenuModule, ForgeTooltipModule, MenuComponent } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { MetadataActionService, MetadataService } from "@tylertech/metadata-module";
import { Utilities } from "@tylertech/shared-module";
import { IMetadataAction } from "@tylertech/tyler-api-types";
import { IWidget } from "./iWidget";

@Component({
    selector: "widget",
    imports: [
        FlexModule,
        FlexLayoutModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeMenuModule,
        ForgeTooltipModule,
        TruncateTextComponent,
        CommonModule,
    ],
    templateUrl: "./widget.tmpl.html",
    styleUrls: ["./widget.component.scss"],
})
export class WidgetComponent implements OnInit
{
    private $metadataService = inject(MetadataService);
    private $metadataActionService = inject(MetadataActionService);
    private $intentService = inject(IntentService);

    readonly widget = input.required<IWidget>();

    readonly hasCount = computed(() => this.widget().count !== undefined);
    readonly count = computed(() => `${this.widget().count}`);

    readonly hasMenu = computed(() =>
    {
        const widget = this.widget();
        return widget.metadataAction
            && this.$metadataActionService.HasSubitems(widget.metadataAction);
    });

    ngOnInit(): void
    {
        this.loadMenuOptions();
    }

    menuOpened(e: CustomEvent<void>): void
    {
        (e.target as unknown as MenuComponent).activateFirstOption();
    }

    widgetColor(color: string): string
    {
        switch (color)
        {
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-green":
                return "green";
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-magenta":
                return "magenta";
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-tylerblue":
                return "tylerblue";
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-orange":
                return "orange";
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-lightblue":
                return "lightblue";
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-gold":
                return "gold";
            // FIXME - why were we hard-coding AngularJs CSS class names in the backend?
            case "md-fill-blue":
                return "blue";
            default:
                return "tylerblue";
        }
    }

    private _menuOptions: IMenuOption[];
    get menuOptions(): IMenuOption[]
    {
        return this._menuOptions;
    }

    loadMenuOptions(): void
    {
        if (!this.hasMenu())
        {
            return;
        }

        const widget = this.widget();
        const actionSubItems = this.$metadataActionService.GetSubitems(widget.metadataAction);
        this._menuOptions = [];

        for (let i = 0, l = actionSubItems.length; i < l; i++)
        {
            if (this.$metadataActionService.IsHidden(actionSubItems[i], null))
                continue;
            let disabled = this.$metadataActionService.IsDisabled(actionSubItems[i], null);

            this._menuOptions.push({
                value: actionSubItems[i].Label,
                label: this.$metadataService.EvaluateString(actionSubItems[i].Label, null, null),
                disabled: disabled
            });
            if (!this.$metadataActionService.HasSubitems(actionSubItems[i]))
                continue;

            const subMenuItems: IMenuOption[] = [];
            const subItems = this.$metadataActionService.GetSubitems(actionSubItems[i]);
            subItems.forEach((x) =>
            {
                disabled = this.$metadataActionService.IsDisabled(x, null);
                subMenuItems.push({
                    value: x.Label,
                    label: this.$metadataService.EvaluateString(x.Label, parent, null),
                    disabled: disabled
                });
            });
            this._menuOptions[i].options = subMenuItems;
        }
    }

    stopTileClick(e: Event): void
    {
        e.stopPropagation();
    }

    onKeydown($event: KeyboardEvent): void
    {
        if ($event.key === "Enter" || $event.key === " ")
        {
            this.activate($event);
        }
    }

    activate(_$event: Event): void
    {
        const widget = this.widget();
        if (widget.metadataAction)
        {
            this.$metadataActionService.ExecuteAction(null, widget.metadataAction);
        }
        else if (widget.url && widget.url !== "#")
        {
            window.location.href = widget.url;
        }
        else if (widget.mime && widget.action)
        {
            this.$intentService.resolve(widget.mime, widget.action, {});
        }
    }

    menuItemSelected($event: CustomEvent<IMenuSelectEventData<unknown, unknown>>): void
    {
        const actionSubItems = this.$metadataActionService.GetSubitems(this.widget().metadataAction);
        let action: IMetadataAction;

        if (!Utilities.IsNullOrUndefined($event.detail.parentValue))
        {
            action = actionSubItems.filter(x => x.Label === $event.detail.parentValue)[0].SubmenuActions.filter(x => x.Label === $event.detail.value)[0];
        }
        else
        {
            action = actionSubItems.filter(x => x.Label === $event.detail.value)[0];
        }

        this.executeAction(action);
    }

    executeAction(action: IMetadataAction): void
    {
        this.$metadataActionService.ExecuteAction(null, action);
    }
}
